import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface GoogleAnalyticsProps {
  measurementId: string;
  debugMode?: boolean;
  anonymizeIp?: boolean;
  trackPageViews?: boolean;
  trackOutboundLinks?: boolean;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export function GoogleAnalytics({
  measurementId,
  debugMode = false,
  anonymizeIp = true,
  trackPageViews = true,
  trackOutboundLinks = true,
}: GoogleAnalyticsProps) {
  const location = useLocation();
  const initialized = useRef(false);
  const previousPath = useRef<string | null>(null);

  // Initialize GA script
  useEffect(() => {
    if (!measurementId || initialized.current) return;

    // Create dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];

    // Define gtag function (must use arguments, not spread, for GA to work correctly)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.gtag = function() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    // Load the gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    script.onload = () => {
      // Initialize GA
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        debug_mode: debugMode,
        anonymize_ip: anonymizeIp,
        send_page_view: false, // We'll track page views manually for SPA
      });

      initialized.current = true;

      if (debugMode) {
        console.log('[GA] Initialized with measurement ID:', measurementId);
      }
    };

    script.onerror = () => {
      console.error('[GA] Failed to load gtag.js');
    };

    return () => {
      // Cleanup is not really needed for GA, but good practice
      const existingScript = document.querySelector(
        `script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [measurementId, debugMode, anonymizeIp]);

  // Track page views on route change
  useEffect(() => {
    if (!trackPageViews || !initialized.current || !measurementId) return;

    const currentPath = location.pathname + location.search;

    // Only track if path actually changed
    if (previousPath.current === currentPath) return;
    previousPath.current = currentPath;

    window.gtag('event', 'page_view', {
      page_path: currentPath,
      page_title: document.title,
      page_location: window.location.href,
    });

    if (debugMode) {
      console.log('[GA] Page view:', currentPath);
    }
  }, [location, trackPageViews, measurementId, debugMode]);

  // Track outbound link clicks
  useEffect(() => {
    if (!trackOutboundLinks || !initialized.current) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (!link) return;

      const href = link.getAttribute('href');
      if (!href) return;

      // Check if it's an external link
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) {
          window.gtag('event', 'click', {
            event_category: 'outbound',
            event_label: href,
            transport_type: 'beacon',
          });

          if (debugMode) {
            console.log('[GA] Outbound click:', href);
          }
        }
      } catch {
        // Invalid URL, ignore
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [trackOutboundLinks, debugMode]);

  // This component doesn't render anything
  return null;
}

// Utility function to track custom events
export function trackEvent(
  eventName: string,
  parameters?: Record<string, unknown>
) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, parameters);
  }
}

// Utility function to set user properties
export function setUserProperties(properties: Record<string, unknown>) {
  if (typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', properties);
  }
}
